import styled from "@emotion/styled";

import { fontSourceSerif4, theme } from "shared/lib/styles";

import { StyledSlot } from "scmp-app/components/advertisement/ad-slots/ad-slot/styles";
import { IconContainer as ActionBarIconContainer } from "scmp-app/components/content/content-action-bar/styles";
import { ContentItemHomeSecondaryV2 } from "scmp-app/components/content/content-item-render/variants/home-secondary-v2";
import {
  ActionBar as SecondaryActionBar,
  Container as SecondaryContainer,
  CoverImage as SecondaryCoverImage,
  Headline as SecondaryHeadline,
  Summary as SecondarySummary,
} from "scmp-app/components/content/content-item-render/variants/home-secondary-v2/styles";
import { SectionLink } from "scmp-app/components/content/content-sections/styles";

const StyledContentItemHomeSecondaryV2 = styled(ContentItemHomeSecondaryV2)`
  ${SectionLink} {
    color: #333333;
    font-weight: 500;
    font-size: 12px;
    line-height: 100%;
  }

  ${SecondaryHeadline} {
    font-size: 16px;
    line-height: 130%;
    letter-spacing: -0.32px;
  }

  ${SecondarySummary} {
    color: #5a5a5a;
    font-size: 14px;
    line-height: 135%;
  }

  ${SecondaryActionBar} time {
    color: #727272;
    font-size: 11px;
  }

  ${ActionBarIconContainer} {
    display: none;

    ${theme.breakpoints.up("tablet")} {
      display: flex;
    }
  }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  inline-size: 100%;

  border-block-start: 1px #000000 solid;

  padding-block-start: 12px;
`;

export const SectionTitle = styled.div`
  padding-block-end: 10px;

  color: #000000;
  font-weight: 400;
  font-size: 28px;
  font-family: ${fontSourceSerif4};
  line-height: 90%;
  letter-spacing: -1.4px;
`;

export const ItemsContainer = styled.div`
  display: grid;
  grid:
    "main-item" min-content
    "left-1" min-content
    "left-2" min-content
    "left-3" min-content
    "bottom-1" min-content
    "bottom-2" min-content
    "right-1" min-content
    "right-2" min-content
    "right-3" min-content
    "sponsor" min-content
    / 100%;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "left-1 divider-1 main-item" min-content
      "left-2 divider-1 main-item" min-content
      "left-3 divider-1 main-item" min-content
      "divider-3 divider-3 divider-3" min-content
      "bottom-1 divider-2 bottom-2" min-content
      "divider-5 divider-5 divider-5" min-content
      "right-1 divider-4 right-3" min-content
      "right-2 divider-4 sponsor" min-content
      / 1fr 1px 1fr;
    gap: 16px 24px;
  }

  ${theme.breakpoints.up("desktop")} {
    display: flex;
    flex-direction: row;
    gap: 0;
    align-items: flex-start;
  }
`;

export const LeftMainSection = styled.div`
  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: flex;
    flex: 1;
    flex-direction: column;

    min-inline-size: 0;
    margin-inline-end: 24px;
  }
`;

export const TopRow = styled.div`
  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: flex;
    flex-direction: row;

    margin-block-end: 16px;
  }
`;

export const LeftColumn = styled.div`
  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: flex;
    flex: 2;
    flex-direction: column;
    row-gap: 16px;

    min-inline-size: 0;
  }
`;

export const BottomRow = styled.div`
  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: flex;
    flex-direction: row;
  }
`;

export const RightColumn = styled.div`
  display: contents;

  ${theme.breakpoints.up("desktop")} {
    display: flex;
    flex: 0 0 300px;
    flex-direction: column;
    row-gap: 16px;

    min-inline-size: 0;
    margin-inline-start: 24px;
  }
`;

const HorizontalDivider = styled.div`
  inline-size: 100%;
  block-size: 0;
  border-block-start: 1px #dfdfdf solid;
`;

const VerticalDivider = styled.div`
  inline-size: 0;
  block-size: 100%;
  border-inline-start: 1px #dfdfdf solid;
`;

export const Divider1 = styled(VerticalDivider)`
  grid-area: divider-1;

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }

  ${theme.breakpoints.up("desktop")} {
    align-self: stretch;

    block-size: auto;
  }
`;

export const Divider2 = styled(VerticalDivider)`
  grid-area: divider-2;

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }

  ${theme.breakpoints.up("desktop")} {
    align-self: stretch;

    block-size: auto;
  }
`;

export const Divider3 = styled(HorizontalDivider)`
  grid-area: divider-3;

  ${theme.breakpoints.only("mobile")} {
    display: none;
  }

  ${theme.breakpoints.up("desktop")} {
    margin-block-end: 16px;
  }
`;

export const Divider4 = styled(VerticalDivider)`
  grid-area: divider-4;

  ${theme.breakpoints.not("tablet")} {
    display: none;
  }
`;

export const Divider5 = styled(HorizontalDivider)`
  grid-area: divider-5;

  ${theme.breakpoints.not("tablet")} {
    display: none;
  }
`;

export const MainItem = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: main-item;

  ${theme.breakpoints.up("tablet")} {
    grid:
      "header" min-content
      "headline" min-content
      "subHeadline" min-content
      "summary" min-content
      "action-bar" min-content
      "cover-image" min-content
      / 100%;
  }

  ${theme.breakpoints.up("desktop")} {
    flex: 3;

    min-inline-size: 0;
    margin-inline-start: 20px;
    padding-inline-start: 20px;
    border-inline-start: 1px #dfdfdf solid;
  }

  ${SecondaryHeadline} {
    font-weight: 600;
    font-size: 18px;
    font-style: normal;
    line-height: 120%;
    letter-spacing: -0.36px;

    ${theme.breakpoints.up("tablet")} {
      font-size: 20px;
      letter-spacing: -0.4px;
    }
  }

  ${SecondarySummary} {
    ${theme.breakpoints.up("tablet")} {
      display: none;
    }
  }

  ${SecondaryCoverImage} {
    margin-block-start: 12px;
  }
`;
MainItem.displayName = "MainItem";

export const LeftItem1 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: left-1;

  ${SecondarySummary} {
    display: none;

    ${theme.breakpoints.up("tablet")} {
      display: block;
    }
  }

  ${SecondaryContainer} {
    grid:
      "header" min-content
      "headline" min-content
      "subHeadline" min-content
      "summary" min-content
      "action-bar" min-content
      "cover-image" min-content
      / 100%;
  }

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;
  }
`;
LeftItem1.displayName = "LeftItem1";

export const LeftItem2 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: left-2;

  padding-block-start: 16px;
  border-block-start: 1px #dfdfdf solid;

  ${SecondarySummary} {
    display: none;

    ${theme.breakpoints.up("tablet")} {
      display: block;
    }
  }

  ${SecondaryContainer} {
    grid:
      "header" min-content
      "headline" min-content
      "subHeadline" min-content
      "summary" min-content
      "action-bar" min-content
      "cover-image" min-content
      / 100%;
  }

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;
  }
`;
LeftItem2.displayName = "LeftItem2";

export const LeftItem3 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: left-3;

  padding-block-start: 16px;
  border-block-start: 1px #dfdfdf solid;

  ${SecondarySummary} {
    display: none;

    ${theme.breakpoints.up("tablet")} {
      display: block;
    }
  }

  ${SecondaryContainer} {
    grid:
      "header" min-content
      "headline" min-content
      "subHeadline" min-content
      "summary" min-content
      "action-bar" min-content
      "cover-image" min-content
      / 100%;
  }

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;
  }
`;
LeftItem3.displayName = "LeftItem3";

export const RightItem1 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: right-1;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;
  }
`;
RightItem1.displayName = "RightItem1";

export const RightItem2 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: right-2;

  padding-block-start: 16px;
  border-block-start: 1px #dfdfdf solid;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;
  }
`;
RightItem2.displayName = "RightItem2";

export const RightItem3 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: right-3;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;
  }

  ${theme.breakpoints.not("tablet")} {
    padding-block-start: 16px;
    border-block-start: 1px #dfdfdf solid;
  }
`;
RightItem3.displayName = "RightItem3";

export const BottomItem1 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: bottom-1;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;

    ${SecondaryCoverImage} {
      display: none;
    }
  }

  ${theme.breakpoints.up("tablet")} {
    grid-template-columns: minmax(0, 1fr) 144px;
    column-gap: 12px;

    ${SecondaryCoverImage} {
      inline-size: 144px;
      block-size: auto;
      aspect-ratio: 3 / 2;
    }
  }

  ${theme.breakpoints.up("desktop")} {
    flex: 1;

    min-inline-size: 0;
    padding-inline-end: 20px;
  }
`;
BottomItem1.displayName = "BottomItem1";

export const BottomItem2 = styled(StyledContentItemHomeSecondaryV2)`
  grid-area: bottom-2;

  ${theme.breakpoints.only("mobile")} {
    margin-block-start: 16px;
    padding-block-start: 12px;
    border-block-start: 1px #dfdfdf solid;

    ${SecondaryCoverImage} {
      display: none;
    }
  }

  ${theme.breakpoints.up("tablet")} {
    grid-template-columns: minmax(0, 1fr) 144px;
    column-gap: 12px;

    ${SecondaryCoverImage} {
      inline-size: 144px;
      block-size: auto;
      aspect-ratio: 3 / 2;
    }
  }

  ${theme.breakpoints.up("desktop")} {
    flex: 1;

    min-inline-size: 0;
    padding-inline-start: 20px;
  }
`;
BottomItem2.displayName = "BottomItem2";

export const SponsorItem = styled.div`
  grid-area: sponsor;

  margin-block-start: 16px;
  padding-block-start: 16px;
  border-block-start: 1px #dfdfdf solid;

  ${theme.breakpoints.up("tablet")} {
    margin-block-start: 0;
    padding-block-start: 16px;
  }

  ${StyledSlot} {
    align-items: self-start;
  }
`;
